import React from "react";
import { Link } from "react-router-dom";
import { useApp, t } from "@/lib/AppContext";
import { resolveImg } from "@/lib/api";
import { MessageCircle, Send, MapPin, Phone, Mail, Clock } from "lucide-react";

export default function Footer() {
  const { content, settings } = useApp();
  const description = t(content, "footer.description", "");
  const copyright = t(content, "footer.copyright", "© 2025 Везём авто из Европы");

  // Build contact items only for non-empty fields
  const contactItems = [
    settings?.phone && { Icon: Phone, label: settings.phone, href: `tel:${settings.phone.replace(/[^+\d]/g, "")}` },
    settings?.email && { Icon: Mail, label: settings.email, href: `mailto:${settings.email}` },
    settings?.address && { Icon: MapPin, label: settings.address, href: null },
    settings?.work_hours && { Icon: Clock, label: settings.work_hours, href: null },
  ].filter(Boolean);

  const hasMessengers = settings?.telegram_url || settings?.whatsapp_url;
  const showContacts = contactItems.length > 0;

  return (
    <footer style={{ background: "var(--color-bg-dark)", color: "rgba(255,255,255,0.80)" }} data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className={`grid grid-cols-1 ${showContacts ? "md:grid-cols-3 lg:grid-cols-4" : "md:grid-cols-2 lg:grid-cols-3"} gap-8 md:gap-10`}>
          {/* Brand */}
          <div className={showContacts ? "md:col-span-3 lg:col-span-2" : "md:col-span-2"}>
            <div className="flex items-center gap-2 mb-4">
              {settings?.logo_url ? (
                <img src={resolveImg(settings.logo_url)} alt={t(content, "header.site_name", "Везём авто из Европы")} className="h-20 w-auto object-contain" />
              ) : (
                <>
                  <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "var(--color-primary)" }}>
                    <span className="font-headings font-black text-white text-lg leading-none">В</span>
                  </div>
                  <div>
                    <div className="font-headings font-extrabold text-white text-base">{t(content, "header.site_name", "Везём авто из Европы")}</div>
                    <div className="font-accent text-xs uppercase tracking-widest" style={{ color: "var(--color-gold)" }}>Европейские авто</div>
                  </div>
                </>
              )}
            </div>
            <p className="text-sm leading-relaxed max-w-md">{description}</p>
            {hasMessengers && (
              <div className="flex gap-3 mt-5">
                {settings?.telegram_url && (
                  <a href={settings.telegram_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-[var(--color-gold)] flex items-center justify-center transition-colors" aria-label="Telegram" data-testid="footer-telegram">
                    <Send size={18} className="text-white" />
                  </a>
                )}
                {settings?.whatsapp_url && (
                  <a href={settings.whatsapp_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-[var(--color-gold)] flex items-center justify-center transition-colors" aria-label="WhatsApp" data-testid="footer-whatsapp">
                    <MessageCircle size={18} className="text-white" />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Nav */}
          <div>
            <h4 className="font-headings font-bold text-white text-sm uppercase tracking-widest mb-4">Разделы</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/catalog" className="hover:text-[var(--color-gold)] transition-colors">Каталог</Link></li>
              <li><Link to="/configurator" className="hover:text-[var(--color-gold)] transition-colors">Подбор авто</Link></li>
              <li><Link to="/services" className="hover:text-[var(--color-gold)] transition-colors">Услуги</Link></li>
              <li><Link to="/reviews" className="hover:text-[var(--color-gold)] transition-colors">Отзывы</Link></li>
              <li><Link to="/blog" className="hover:text-[var(--color-gold)] transition-colors">Новости</Link></li>
              <li><Link to="/about" className="hover:text-[var(--color-gold)] transition-colors">О компании</Link></li>
              <li><Link to="/contacts" className="hover:text-[var(--color-gold)] transition-colors">Контакты</Link></li>
            </ul>
          </div>

          {/* Contacts (only if any field present) */}
          {showContacts && (
            <div data-testid="footer-contacts-block">
              <h4 className="font-headings font-bold text-white text-sm uppercase tracking-widest mb-4">Контакты</h4>
              <ul className="space-y-3 text-sm">
                {contactItems.map(({ Icon, label, href }, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Icon size={16} className="mt-0.5 shrink-0" style={{ color: "var(--color-gold)" }} />
                    {href ? <a href={href} className="hover:text-white break-all">{label}</a> : <span>{label}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="mt-10 mb-6" style={{ height: 2, background: "var(--color-primary)" }} />
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 text-xs text-white/60">
          <span data-testid="footer-copyright">{copyright}</span>
        </div>
      </div>
      <div className="tricolor-strip" aria-hidden="true">
        <div /><div /><div />
      </div>
    </footer>
  );
}
